import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";

interface Props { payload: VisualPayload }

const LEAGUE_LABELS: Record<string, string> = {
  great:  "Great League",
  ultra:  "Ultra League",
  master: "Master League",
};

const LEAGUE_ACCENT: Record<string, string> = {
  great:  "text-green-400",
  ultra:  "text-blue-400",
  master: "text-purple-400",
};

export default function BestIVsCard({ payload }: Props) {
  const sections = payload.sections.filter((s) => s.section === "best_iv");

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {sections.length === 0 ? (
        <p className="text-sm text-white/40">No IV data available.</p>
      ) : (
        <div className="space-y-4">
          {sections.map((sec) => {
            const league = sec.league as string;
            const label = LEAGUE_LABELS[league] ?? league;
            const accent = LEAGUE_ACCENT[league] ?? "text-white/60";
            return (
              <div key={league} className="bg-white/[0.03] rounded-xl p-3 border border-white/[0.06]">
                {/* League name */}
                <p className={`text-[11px] font-semibold uppercase tracking-wide mb-2 ${accent}`}>{label}</p>

                {/* IV triple hero */}
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-2xl font-bold text-scout-yellow tabular-nums">
                    {sec.iv_attack as number}/{sec.iv_defense as number}/{sec.iv_stamina as number}
                  </span>
                  <span className="text-xs text-white/30 ml-1">Rank 1</span>
                </div>

                {/* IV breakdown */}
                <div className="grid grid-cols-3 gap-1.5 mb-2">
                  {([
                    ["ATK", sec.iv_attack as number],
                    ["DEF", sec.iv_defense as number],
                    ["STA", sec.iv_stamina as number],
                  ] as [string, number][]).map(([lbl, val]) => (
                    <div key={lbl} className="bg-white/[0.04] rounded-lg py-2 text-center">
                      <p className="text-[10px] text-white/30 uppercase tracking-wide">{lbl}</p>
                      <p className="text-base font-bold text-white mt-0.5">{val}</p>
                      <p className="text-[9px] text-white/25">/15</p>
                    </div>
                  ))}
                </div>

                {/* Footer: Level · CP · SP */}
                <div className="flex items-center gap-2 text-[11px] text-white/35">
                  <span>Lv {sec.level as number}</span>
                  <span className="text-white/20">·</span>
                  <span>CP {(sec.cp as number).toLocaleString()}</span>
                  <span className="text-white/20">·</span>
                  <span>SP {(sec.stat_product as number).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
}
